import Vue, { defineComponent } from 'vue'
import moment from 'moment/moment'
import { mapGetters, mapState } from 'vuex'
import Container from 'typedi'
import Notification from '@/modules/common/services/notification.service'
import SettingApplicator from '@/modules/common/components/ui-core/setting-applicator/setting-applicator.vue'
import SettingChips from '@/modules/common/components/ui-core/setting-chips/setting-chips.vue'

export default defineComponent({
  name: 'SettingsFields',
  components: {
    'setting-applicator': SettingApplicator,
    'setting-chips': SettingChips
  },
  props: {
    value: {
      required: true
    },
    defaultOnlySettings: {
      required: false,
      type: Array,
      default: () => []
    },
    settingsFields: {
      required: false,
      type: Object,
      default: () => ({})
    },
    fieldsPrint: {
      required: false,
      type: Array,
      default: () => []
    },
    permissions: {
      required: true,
      type: String,
      default: ''
    },
    eventUuid: {
      required: true,
      type: String,
      default: ''
    },
    companyUuid: {
      required: false,
      type: String,
      default: ''
    },
    displayTabs: {
      required: false,
      type: Array,
      default: () => []
    },
    // TODO(zb): This is to handle a special case for track settings. Review this later.
    sessionExportFields: {
      required: false,
      type: Array,
      default: () => []
    },
    sessionFields: {
      required: false,
      type: Array,
      default: () => []
    },
    pageOptions: {
      required: false,
      type: Array,
      default: () => []
    },
    blacklistOptions: {
      required: false,
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      tab: '',
      languageToUse: 'en',
      registrationTypeKey: '_default',
      operatorFunctions: {
        '=': (a, b) => a === b,
        '!=': (a, b) => a !== b,
        '>': (a, b) => parseInt(a) > parseInt(b),
        '<': (a, b) => parseInt(a) < parseInt(b),
        '>=': (a, b) => parseInt(a) >= parseInt(b),
        '<=': (a, b) => parseInt(a) <= parseInt(b),
        '^=': (a, b) => a.includes(b)
      },
      valueType: [
        { text: 'Field', value: 'field' },
        { text: 'Literal', value: 'literal' }
      ]
    }
  },
  computed: {
    ...mapState('setting', ['providerFields']),
    ...mapState('adminUser', ['users']),
    ...mapGetters('option', ['registrationTypes']),
    pages (): string[] {
      if ((this as any).displayTabs.length > 0) {
        return Object.keys((this as any).value).filter((page: string) => (this as any).displayTabs.includes(page.toLowerCase()))
      }
      return Object.keys((this as any).value)
    },
    settingsObject: {
      get () {
        return (this as any).value
      },
      set (value: any) {
        (this as any).$emit('input', value)
      }
    }
  },
  watch: {
    settingsObject: {
      deep: true,
      handler (payload: any) {
        (this as any).settingsObject = payload
      }
    }
  },
  mounted () {
    (this as any).tab = (this as any).pages[0]
  },
  methods: {
    commonVBind (setting: any) {
      return {
        dense: true,
        'hide-details': true,
        outlined: true,
        disabled: !(this as any).hasPermissions(setting.name, 'edit')
      }
    },
    initLeadsShowFields (setting: any) {
      if (setting._leadsInitDone) return true
      const val: string = (setting.value._default ?? '').toString().trim()
      const normalized = val.length ? val.toLowerCase() : 'email,phone_number'
      const parts = normalized.split(',').map((s: string) => s.trim()).filter(Boolean)
      const hasEmail = parts.includes('email')
      const hasPhone = parts.includes('phone_number')
      const isNone = parts.includes('none') || (!hasEmail && !hasPhone)
      setting._email = !isNone && hasEmail
      setting._phone = !isNone && hasPhone
      setting._leadsInitDone = true
      return true
    },
    syncLeadsShowFields (setting: any) {
      const email = !!setting._email
      const phone = !!setting._phone
      let value = 'none'
      if (email && phone) {
        value = 'email,phone_number'
      } else if (email) {
        value = 'email'
      } else if (phone) {
        value = 'phone_number'
      }
      setting.value._default = value
    },
    resetValue (setting: any, registration_type: any) {
      setting[registration_type] = null
    },
    getItemsOfFontByTemplate (template = 'default'): Array<{ value: string; text: string }> {
      const fonts: any[] = []
      const designFields = (this as any).$store.state.design.designFields
      if (designFields) {
        if (designFields[template]) {
          Object.keys(designFields[template]).map((key: any) => {
            if (key.split('_')[0] === template && designFields[template][key].type === 'font') {
              Object.keys(designFields[template][key].options).map((option: any) => {
                const font: any = {
                  value: option,
                  text: designFields[template][key].options[option]
                }
                if (!fonts.includes(font)) {
                  fonts.push(font)
                }
              })
            }
          })
        }
      }
      return fonts
    },
    getSettingName (setting: any, key: any) {
      if ('display_name' in setting && setting.display_name) {
        return setting.display_name
      } else if ('name' in setting && setting.name) {
        return setting.name
      }
      return key
    },
    getShowCondition (setting: any) {
      if ('show_field' in setting) {
        return setting.show_field
      }
      return true
    },
    async loadFields (provider: any, table: any) {
      (this as any).$store.dispatch('setting/getProviderFields', { event_uuid: (this as any).eventUuid, provider, table })
    },
    showSettingDefault ({ default: settingDefault, type }: any) {
      return ![null, undefined, ''].includes(settingDefault) && !['fields', 'fieldswithwidth', 'fieldmapping', 'localized', 'localized_html'].includes(type)
    },
    registrationTypesToDisplay (setting: any) {
      if ((this as any).defaultOnlySettings.includes(setting)) {
        return (this as any).registrationTypes.filter((registration_type: any) => registration_type.value === '_default')
      }
      return (this as any).registrationTypes
    },
    regTypeName (value: string) {
      const _value: any = (this as any).registrationTypes.filter((reg_type: any) => reg_type.value === value)
      if (_value.length) {
        return (this as any).capitalizeText(_value[0].text)
      }
    },
    capitalizeText (value: string) {
      return value.replace(/\w\S*/g, (w) => (w.replace(/^\w/, (c) => c.toUpperCase())))
    },
    async reUseValue (setting: any, value: any) {
      if (setting.type === 'localized_html' || setting.type === 'localized') {
        setting.temp = value[(this as any).languageToUse]
      } else if (setting.type === 'fields' || setting.type === 'fieldmapping' || setting.type === 'field') {
        setting.temp_field = value.field
        setting.temp_label = value.label[(this as any).languageToUse]
      } else {
        setting.temp = value
      }
    },
    removeField (setting: any, reg_type: any, field: any, singleFieldSetting = false) {
      // Objects are shallow copies, so just modifying the passed setting will work here.
      if (singleFieldSetting) {
        setting.value[reg_type] = null
      } else {
        setting.value[reg_type] = setting.value[reg_type].filter((value: any) => {
          if (value.field !== field.field) {
            return true
          }
        })
      }
    },
    selectOptions (setting: any) {
      if (setting.type === 'admin_uuid') {
        return (this as any).users.filter((user: any) => (user.company && user.company.uuid === (this as any).companyUuid)).map((user: any) => ({ text: user.name, value: user.uuid }))
      } else if (setting.type === 'page') {
        return (this as any).pageOptions
      } else if (setting.type === 'blacklists') {
        return (this as any).blacklistOptions
      } else {
        return setting.name === 'default_question_label_formatting' ? setting.values : (this as any).convertSettingValuesToItemsArray(setting.selectItems)
      }
    },
    convertSettingValuesToItemsArray (obj: any) {
      const arr: any = []
      for (const key in obj) {
        arr.push({
          text: obj[key],
          value: key
        })
      }
      return arr
    },
    getRegTypeValues (values: any) {
      const regValues: any = {}
      for (const value in values) {
        if (values[value] && typeof values[value] !== 'object' && !(values[value] instanceof File)) {
          regValues[value] = values[value]
        }
      }
      return regValues
    },
    formatValue (value: any) {
      if (value instanceof Date) {
        return moment(value).format('YYYY-MM-DD HH:mm')
      } else {
        return value
      }
    },
    setRegTypeValue (setting: any, reg_type: any, tab: any) {
      if (setting.type === 'localized' || setting.type === 'localized_html') {
        if (setting.temp !== null) {
          setting.value[reg_type.value] = {
            [(this as any).languageToUse]: setting.temp
          }
        }
      } else if (setting.type === 'fields') {
        if (setting.temp_field && setting.temp_label) {
          const fieldType = ((this as any).fieldsPrint as Record<string, any>).find((field: any) => field.value === setting.temp_field)?.type ?? 'field'
          setting.error = ''
          const reg_type_values = setting.value[reg_type.value]
          const temp: any = []
          reg_type_values.forEach((value: any) => {
            if (value.field !== setting.temp_field) {
              temp.push(value)
            }
          })
          temp.push({ type: fieldType, field: setting.temp_field, label: { en: setting.temp_label } })
          setting.value[reg_type.value] = temp
        } else {
          setting.error = 'Both field and label are required.'
        }
      } else if (setting.type === 'field') {
        if (setting.temp_field && setting.temp_label) {
          setting.error = ''
          const temp: any = {
            field: setting.temp_field,
            label: {
              en: setting.temp_label
            }
          }
          setting.value[reg_type.value] = temp
          setting.temp_field = null
          setting.temp_label = null
        } else {
          setting.error = 'Both field and label are required.'
        }
      } else if (setting.type === 'fieldswithwidth') {
        if (setting.temp_field && setting.temp_label && setting.temp_width) {
          setting.error = ''
          const reg_type_values = setting.value[reg_type.value]
          const temp: any = []
          reg_type_values.forEach((value: any) => {
            if (value.field !== setting.temp_field) {
              temp.push(value)
            }
          })
          temp.push({
            type: 'field',
            field: setting.temp_field,
            label: { en: setting.temp_label },
            width: parseInt(setting.temp_width)
          });
          (this as any).settingsObject[tab][setting.name].value[reg_type.value] = temp
        }
      } else if (setting.type === 'fieldmapping') {
        if (setting.temp_field && setting.temp_external_field && setting.temp_value_type) {
          const reg_type_values = setting.value[reg_type.value]
          const temp: any = []
          reg_type_values.forEach((value: any) => {
            if (value.field !== setting.temp_field) {
              temp.push(value)
            }
          })
          temp.push({ external_field: setting.temp_external_field, field: setting.temp_field, valueType: setting.temp_value_type })
          setting.value[reg_type.value] = temp
        }
      } else if (setting.type === 'integer') {
        if (setting.temp !== null) {
          // TODO: clean this up. This function is hard to read as it is.
          // attempt to parse setting.temp to an int.
          const value = parseInt(setting.temp)
          // if parseInt is NaN show a notification.
          if (isNaN(value)) {
            Container.get(Notification).error(`Please enter a valid number in the ${setting}`)
          } else {
            // Then assign to the this.settingsObject[tab][setting.name].value[reg_type.value] to that value.
            setting.value[reg_type.value] = value
          }
        }
      } else {
        if (setting.temp !== null && setting.type !== 'localized_html') {
          setting.value[reg_type.value] = setting.temp
        }
      }
    },
    registrationTypesValuesToDisplay (setting: any, valueObject: any) {
      if ((this as any).defaultOnlySettings.includes(setting)) {
        for (const registration_type in valueObject) {
          if (registration_type !== '_default') {
            delete valueObject[registration_type]
          }
        }
      }
      return valueObject
    },
    showOrHideSettingsFields () {
      const settings_with_show_if: any = []
      for (const setting in (this as any).settingsObject) {
        if ((this as any).settingsObject[setting].hasOwnProperty('show_if')) {
          settings_with_show_if.push((this as any).settingsObject[setting])
        }
      }
      for (let i = 0; i < settings_with_show_if.length; i++) {
        const setting_name = settings_with_show_if[i].name
        const setting_show_if = settings_with_show_if[i].show_if
        const show_if_field = setting_show_if.field
        const show_if_operator = setting_show_if.operator
        const show_if_value = setting_show_if.value

        if (
          (this as any).settingsObject[show_if_field].type === 'localized' ||
          (this as any).settingsObject[show_if_field].type === 'localized_html'
        ) {
          (this as any).settingsObject[setting_name].show_field = (this as any).operatorFunctions[show_if_operator](
            (this as any).settingsObject[show_if_field].value[(this as any).registrationTypeKey][(this as any).languageToUse],
            show_if_value
          )
        } else {
          (this as any).settingsObject[setting_name].show_field = (this as any).operatorFunctions[show_if_operator](
            (this as any).settingsObject[show_if_field].value[(this as any).registrationTypeKey],
            show_if_value
          )
        }
      }
    },
    findValueByKey (obj, targetKey) {
      // Base case: check if the object itself is null or not an object
      if (obj === null || typeof obj !== 'object') {
        return undefined
      }

      // Check if the key exists at the current level
      if (targetKey in obj) {
        return obj[targetKey]
      }

      // Recursively search through nested objects and arrays
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const result = this.findValueByKey(obj[key], targetKey)
          if (result !== undefined) {
            return result // Return immediately if found deep within
          }
        }
      }

      return undefined
    },
    hasPermissionForSetting (settingName: string, action: string): boolean {
      const settingPermission = this.findValueByKey(this.settingsFields, settingName)?.permissions?.[action]
      if (settingPermission === undefined || settingPermission === null) {
        return true
      }
      return this.$hasPermission(settingPermission)
    },
    hasPermissions (setting, action: string) {
      return this.$hasPermission(this.permissions) && this.hasPermissionForSetting(setting.name, action)
    }
  }
})
